import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

// Horizontal scroll container with mask-image edge fades (left when scrolled, right until the end)
// and an optional one-time animated chevron hint (dismissed after first scroll, remembered in localStorage).
export default function ScrollFade({ children, className = "", hintKey, fade = 40, testId }) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });
  const [hint, setHint] = useState(() => !!hintKey && localStorage.getItem(hintKey) !== "1");
  const measure = useCallback(() => {
    const el = ref.current; if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ start: el.scrollLeft <= 2, end: max <= 2 || el.scrollLeft >= max - 2 });
  }, []);
  useEffect(() => {
    measure();
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(measure); ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [measure, children]);
  const onScroll = () => { measure(); if (hint) { setHint(false); localStorage.setItem(hintKey, "1"); } };
  const l = edges.start ? "0px" : `${fade}px`, r = edges.end ? "0px" : `${fade}px`;
  const mask = `linear-gradient(90deg, transparent 0, #111111 ${l}, #111111 calc(100% - ${r}), transparent 100%)`;
  return (
    <div className="relative">
      <div ref={ref} data-testid={testId} onScroll={onScroll} className={`overflow-x-auto no-scrollbar ${className}`} style={{ WebkitOverflowScrolling: "touch", maskImage: mask, WebkitMaskImage: mask }}>
        {children}
      </div>
      {hint && !edges.end && (
        <span data-testid={`${testId || "scroll"}-hint`} aria-hidden className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full border border-[#D4AF37]/60 bg-[#111]/90 text-[#D4AF37] scroll-hint-nudge">
          <ChevronRight size={14} />
        </span>
      )}
    </div>
  );
}
